import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Users, Mail, Phone, Calendar, ShieldCheck, RefreshCw } from "lucide-react";
import api from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "-"; }
}

export default function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  const [totalConsent, setTotalConsent] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/contacts");
      setContacts(data.contacts || []);
      setTotalConsent(data.total_consent || 0);
      setTotalVisits(data.total_visits || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento contatti"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("vm_token");
      const res = await fetch(`${API}/api/owner/contacts/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Errore export");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `contatti_${new Date().toISOString().slice(0,10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) { toast.error(e?.message || "Errore download"); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="contacts-panel" className="bg-white rounded-3xl border border-[#E8E2D5] shadow-sm overflow-hidden">
      <div className="p-5 pb-4 border-b border-[#E8E2D5] flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#3B503A] to-[#2f4030] grid place-items-center text-white shadow"><Users size={20}/></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Marketing & Recensioni</div>
          <h3 className="font-serif-display text-2xl font-bold">Contatti raccolti</h3>
        </div>
        <button data-testid="contacts-refresh" onClick={load} disabled={loading} className="w-9 h-9 rounded-full bg-[#F0EBE1] grid place-items-center text-[#231F1D] disabled:opacity-50" title="Aggiorna"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        <button data-testid="contacts-download-csv" onClick={downloadCsv} disabled={downloading || contacts.length === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white text-sm font-bold shadow disabled:opacity-50">
          <Download size={14}/> {downloading ? "Scarico..." : "Scarica CSV"}
        </button>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="stat-consent" className="rounded-2xl bg-gradient-to-br from-[#EAF0E9] to-white border border-[#3B503A]/20 p-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#3B503A] text-white grid place-items-center"><ShieldCheck size={16}/></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#3B503A] font-black">Con consenso</div>
                <div className="font-serif-display text-2xl font-black text-[#231F1D]">{totalConsent}</div>
              </div>
            </div>
            <div className="text-[10px] text-[#6B625B] mt-1">Autorizzati al trattamento dati per marketing</div>
          </div>
          <div data-testid="stat-visits" className="rounded-2xl bg-gradient-to-br from-[#FDECE4] to-white border border-[#C85A32]/20 p-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#C85A32] text-white grid place-items-center"><Users size={16}/></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-black">Registrazioni totali</div>
                <div className="font-serif-display text-2xl font-black text-[#231F1D]">{totalVisits}</div>
              </div>
            </div>
            <div className="text-[10px] text-[#6B625B] mt-1">Include anche i guest senza consenso privacy</div>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-[#6B625B] text-sm">Caricamento contatti...</div>
        ) : contacts.length === 0 ? (
          <div data-testid="contacts-empty" className="py-10 text-center border border-dashed border-[#E8E2D5] rounded-2xl">
            <Users className="mx-auto text-[#B8AFA5]" size={26}/>
            <div className="mt-2 font-serif-display text-lg text-[#231F1D]">Nessun contatto raccolto</div>
            <p className="text-xs text-[#6B625B] mt-1 max-w-xs mx-auto">Quando i guest si registreranno con il consenso privacy, li vedrai qui.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#E8E2D5] overflow-hidden">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#FBF9F5] text-[#6B625B]">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest">Nome</th>
                    <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest">Contatto</th>
                    <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest hidden sm:table-cell">Telefono</th>
                    <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest hidden md:table-cell">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-t border-[#E8E2D5] hover:bg-[#FBF9F5]">
                      <td className="px-3 py-2.5 font-semibold text-[#231F1D]">
                        {c.name}
                        {c.table_number && <span className="ml-1 text-[10px] text-[#8C580C] font-bold">· T{c.table_number}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-[#3B503A]"><span className="inline-flex items-center gap-1 text-xs"><Mail size={11}/> {c.contact || "-"}</span></td>
                      <td className="px-3 py-2.5 text-[#3B503A] hidden sm:table-cell"><span className="inline-flex items-center gap-1 text-xs"><Phone size={11}/> {c.phone || "-"}</span></td>
                      <td className="px-3 py-2.5 text-[#6B625B] text-xs hidden md:table-cell"><span className="inline-flex items-center gap-1"><Calendar size={11}/> {fmtDate(c.joined_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-[11px] text-[#6B625B] leading-relaxed p-3 rounded-2xl bg-[#FEF3D6]/50 border border-[#D99B26]/30">
          <b>Nota GDPR:</b> qui vedi solo i guest che hanno autorizzato esplicitamente il trattamento dei loro dati per finalità di marketing (Reg. UE 2016/679). Puoi utilizzare questa lista per invii promozionali, newsletter e comunicazioni del tuo locale.
        </div>
      </div>
    </div>
  );
}
